import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './WhatsAppButton.css';

// Both numbers — first number is the primary dialing target
const PRIMARY_NUMBER = '918109849232';   // +91 81098 49232
const SECONDARY_NUMBER = '919425122751'; // +91 94251 22751

const WELCOME_MESSAGE = encodeURIComponent(
  'Hello! 👋 I found The Unexplored Routes website and I\'m interested in planning an offbeat trip. Could you please share more details about your upcoming tours?'
);

export default function WhatsAppButton() {
  const [showPopup, setShowPopup] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasShownAuto, setHasShownAuto] = useState(false);

  // Auto-show popup after 8 seconds on first visit
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasShownAuto) {
        setShowPopup(true);
        setHasShownAuto(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [hasShownAuto]);

  // Auto-hide popup after 12 seconds if user doesn't interact
  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => setShowPopup(false), 12000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  const handleMainClick = () => {
    // On mobile — open WhatsApp directly; on desktop — show popup
    if (window.innerWidth <= 768) {
      openWhatsApp(PRIMARY_NUMBER);
    } else {
      setShowPopup(prev => !prev);
    }
  };

  const openWhatsApp = (number) => {
    window.open(
      `https://wa.me/${number}?text=${WELCOME_MESSAGE}`,
      '_blank',
      'noopener,noreferrer'
    );
    setShowPopup(false);
  };

  return (
    <div className="wa-wrapper">
      {/* Popup card */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="wa-popup"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.88 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
          >
            {/* Header */}
            <div className="wa-popup-header">
              <div className="wa-popup-avatar">
                <svg viewBox="0 0 24 24" fill="white" width="26" height="26">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div className="wa-popup-title">
                <strong>The Unexplored Routes</strong>
                <span>
                  <span className="wa-online-dot" />
                  Typically replies instantly
                </span>
              </div>
              <button
                className="wa-popup-close"
                onClick={() => setShowPopup(false)}
                aria-label="Close WhatsApp chat"
              >
                ✕
              </button>
            </div>

            {/* Message bubble */}
            <div className="wa-popup-body">
              <div className="wa-bubble">
                <p>👋 Hi there! Welcome to <strong>The Unexplored Routes</strong></p>
                <p>Planning an offbeat adventure? We'd love to help you explore Ladakh, Kashmir, Meghalaya, Kerala, Spiti Valley & more!</p>
                <p>Tap below to chat with us on WhatsApp 👇</p>
              </div>
            </div>

            {/* Number buttons */}
            <div className="wa-popup-actions">
              <button
                className="wa-number-btn"
                onClick={() => openWhatsApp(PRIMARY_NUMBER)}
              >
                <span className="wa-number-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
                <div>
                  <span className="wa-num-label">Chat on WhatsApp</span>
                  <span className="wa-num-value">+91 81098 49232</span>
                </div>
              </button>

              <button
                className="wa-number-btn secondary"
                onClick={() => openWhatsApp(SECONDARY_NUMBER)}
              >
                <span className="wa-number-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
                <div>
                  <span className="wa-num-label">Alternate Number</span>
                  <span className="wa-num-value">+91 94251 22751</span>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating WhatsApp button */}
      <motion.button
        className={`wa-fab ${showPopup ? 'active' : ''}`}
        onClick={handleMainClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        aria-label="Chat on WhatsApp"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={showPopup ? {} : { y: [0, -6, 0] }}
        transition={showPopup ? {} : { repeat: Infinity, duration: 2.5, ease: 'easeInOut', repeatDelay: 3 }}
      >
        {/* Ping animation ring */}
        {!showPopup && <span className="wa-ping" />}

        {/* Icon — toggles between WhatsApp and X */}
        <AnimatePresence mode="wait">
          {showPopup ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="wa-fab-icon"
            >
              ✕
            </motion.span>
          ) : (
            <motion.span
              key="wa"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="wa-fab-icon"
            >
              <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </motion.span>
          )}
        </AnimatePresence>

        {/* Tooltip on hover */}
        <AnimatePresence>
          {showTooltip && !showPopup && (
            <motion.span
              className="wa-tooltip"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.2 }}
            >
              Chat with us!
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
